import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getStateForCity } from '../lib/cityStateMap';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import MultiSelectFilter from './MultiSelectFilter';
import api from '../lib/api';
import SecurityWatermark from './SecurityWatermark';
import { isRestricted, maskPhone, maskEmail, maskGST, applyRestrictedSearch, MIN_SEARCH_CHARS } from '../lib/restrictions';

const DistributorManagement = ({ user }) => {
  const restricted = isRestricted(user);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDist, setEditingDist] = useState(null);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCities, setFilterCities] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterAreas, setFilterAreas] = useState([]);
  const [form, setForm] = useState({ company_name: '', contact_person: '', phone: '', email: '', address: '', area: '', city: '', state: '', pincode: '', gst_number: '', discount_percentage: 15 });
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const navigate = useNavigate();

  const handleDownload = async (format) => {
    try {
      const res = await api.get(`/downloads/distributors/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `distributors.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  useEffect(() => { fetchDistributors(); }, []);

  const fetchDistributors = async () => {
    try { const res = await api.get('/distributors'); setDistributors(res.data); }
    catch { toast.error('Failed to load distributors'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'city') {
        const autoState = getStateForCity(value);
        if (autoState && !prev.state) next.state = autoState;
      }
      return next;
    });
    if (name === 'pincode' && value.length === 6) fetchPincodeDetails(value);
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const res = await api.get(`/pincode/${pincode}`);
      setAvailableAreas(res.data.areas || []);
      setForm(prev => ({ ...prev, area: res.data.area || '', city: res.data.city || '', state: res.data.state || '' }));
      toast.success(`Auto-filled: ${res.data.city}, ${res.data.state}`);
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDist) {
        await api.put(`/distributors/${editingDist.id}`, form);
        toast.success('Distributor updated!');
      } else {
        await api.post('/distributors', form);
        toast.success('Distributor created!');
      }
      setIsDialogOpen(false); resetForm(); fetchDistributors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save distributor'); }
  };

  const startEdit = (dist) => {
    setEditingDist(dist);
    setForm({ company_name: dist.company_name, contact_person: dist.contact_person, phone: dist.phone, email: dist.email || '', address: dist.address || '', area: dist.area || '', city: dist.city || '', state: dist.state || '', pincode: dist.pincode || '', gst_number: dist.gst_number || '', discount_percentage: dist.discount_percentage ?? 15 });
    setIsDialogOpen(true);
  };

  const deleteDist = async (id) => {
    if (!window.confirm('Delete this distributor?')) return;
    try { await api.delete(`/distributors/${id}`); toast.success('Distributor deleted'); fetchDistributors(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const resetForm = () => { setForm({ company_name: '', contact_person: '', phone: '', email: '', address: '', area: '', city: '', state: '', pincode: '', gst_number: '', discount_percentage: 15 }); setEditingDist(null); setAvailableAreas([]); };
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`; };

  const cityOptions = [...new Set(distributors.filter(d => !filterStates.length || filterStates.includes(d.state)).map(d => d.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const stateOptions = [...new Set(distributors.map(d => d.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const areaOptions = [...new Set(distributors.map(d => d.area).filter(Boolean))].sort().map(a => ({ value: a, label: a }));
  const hasActiveFilters = !!(searchTerm || filterCities.length || filterStates.length || filterAreas.length);

  const filtered = restricted
    ? applyRestrictedSearch(user, distributors.map(d => ({ ...d, name: d.company_name })), searchTerm)
    : distributors.filter(d => {
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          if (!d.company_name?.toLowerCase().includes(q) && !d.contact_person?.toLowerCase().includes(q) && !d.phone?.includes(searchTerm) && !(d.city || '').toLowerCase().includes(q) && !(d.area || '').toLowerCase().includes(q) && !(d.state || '').toLowerCase().includes(q)) return false;
        }
        if (filterCities.length && !filterCities.includes(d.city)) return false;
        if (filterStates.length && !filterStates.includes(d.state)) return false;
        if (filterAreas.length && !filterAreas.includes(d.area)) return false;
        return true;
      }).sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="distributor-management-container">
      {restricted && <SecurityWatermark user={user} label="DISTRIBUTORS • CONFIDENTIAL" />}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Distributors</h1>
          <p className="text-gray-600 mt-1">Manage your distributor network</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50 gap-1.5" onClick={() => handleDownload('excel')} data-testid="download-distributors-excel">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Excel
              </Button>
              <Button variant="outline" size="sm" className="text-red-700 border-red-300 hover:bg-red-50 gap-1.5" onClick={() => handleDownload('pdf')} data-testid="download-distributors-pdf">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                PDF
              </Button>
            </>
          )}
        {canManage && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDist ? 'Edit Distributor' : 'Add New Distributor'}</DialogTitle>
                <DialogDescription>Distributor company details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Company Name *</Label><Input name="company_name" value={form.company_name} onChange={handleChange} required data-testid="dist-company-input" /></div>
                  <div className="space-y-2"><Label>Contact Person *</Label><Input name="contact_person" value={form.contact_person} onChange={handleChange} required data-testid="dist-contact-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Phone *</Label><Input name="phone" value={form.phone} onChange={handleChange} required data-testid="dist-phone-input" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" value={form.email} onChange={handleChange} data-testid="dist-email-input" /></div>
                </div>
                <div className="space-y-2"><Label>Address</Label><Input name="address" value={form.address} onChange={handleChange} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pincode (auto-fills area/city/state)</Label>
                    <Input name="pincode" value={form.pincode} onChange={handleChange} maxLength="6" data-testid="dist-pincode-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Area</Label>
                    {availableAreas.length > 0 ? (
                      <select name="area" value={form.area} onChange={handleChange} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                        {availableAreas.map((a, i) => <option key={i} value={a}>{a}</option>)}
                      </select>
                    ) : <Input name="area" value={form.area} onChange={handleChange} />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>City</Label><Input name="city" value={form.city} onChange={handleChange} /></div>
                  <div className="space-y-2"><Label>State</Label><Input name="state" value={form.state} onChange={handleChange} /></div>
                </div>
                <div className="space-y-2"><Label>GST Number</Label><Input name="gst_number" value={form.gst_number} onChange={handleChange} data-testid="dist-gst-input" /></div>
                <div className="space-y-2"><Label>Discount %</Label><Input name="discount_percentage" type="number" min="0" max="100" step="0.5" value={form.discount_percentage} onChange={handleChange} data-testid="dist-discount-input" /></div>
                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1" data-testid="submit-dist-button">{editingDist ? 'Update Distributor' : 'Create Distributor'}</Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card data-testid="distributor-filters">
        <CardContent className="p-4 space-y-3">
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by company name, contact person, or phone..." className="max-w-md" data-testid="distributor-search-input" />
          <div className="flex flex-wrap gap-2 items-center">
            <MultiSelectFilter label="City" options={cityOptions} selected={filterCities} onChange={setFilterCities} testId="dist-filter-city" />
            <MultiSelectFilter label="State" options={stateOptions} selected={filterStates} onChange={setFilterStates} testId="dist-filter-state" />
            <MultiSelectFilter label="Area" options={areaOptions} selected={filterAreas} onChange={setFilterAreas} testId="dist-filter-area" />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => { setSearchTerm(''); setFilterCities([]); setFilterStates([]); setFilterAreas([]); }} data-testid="dist-clear-filters">
                Clear All
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500">Showing {filtered.length} of {distributors.length} distributors</p>}
        </CardContent>
      </Card>

      <Card data-testid="distributors-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Distributors ({filtered.length})</CardTitle>
            {canManage && (
              <Button size="sm" className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-sm" onClick={() => setIsDialogOpen(true)} data-testid="add-distributor-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                Add Distributor
              </Button>
            )}
          </div>
          <CardDescription>Your distributor network and partnerships</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="distributors-empty">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              {restricted ? (
                (searchTerm || '').trim().length < MIN_SEARCH_CHARS ? (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Required</h3>
                    <p className="text-gray-600">Type at least {MIN_SEARCH_CHARS} characters to look up a distributor.</p>
                    <p className="text-xs text-gray-400 mt-2">For your role, the full distributor list is not displayed. Up to 10 search results are shown.</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Match</h3>
                    <p className="text-gray-600">No distributor matches "{searchTerm}".</p>
                  </>
                )
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{searchTerm ? 'No Distributors Found' : 'No Distributors Yet'}</h3>
                  <p className="text-gray-600">{searchTerm ? 'Try a different search term.' : 'Add your first distributor.'}</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="distributors-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Contact</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Phone</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Location</th>
                    <th className="p-3 text-left font-semibold text-gray-600">GST</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Discount</th>
                    <th className="p-3 text-left font-semibold text-gray-600">Added</th>
                    <th className="p-3 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(dist => (
                    <tr key={dist.id} className="border-t hover:bg-gray-50" data-testid={`dist-row-${dist.id}`}>
                      <td className="p-3">
                        <div className="font-semibold text-gray-900">{dist.company_name}</div>
                        {dist.email && <div className="text-xs text-gray-400">{restricted ? maskEmail(dist.email) : dist.email}</div>}
                      </td>
                      <td className="p-3 text-gray-700">{dist.contact_person}</td>
                      <td className="p-3 text-gray-700">{restricted ? maskPhone(dist.phone) : dist.phone}</td>
                      <td className="p-3 text-gray-500 text-xs">{[dist.area, dist.city, dist.state].filter(Boolean).join(', ')}{dist.pincode ? ` - ${dist.pincode}` : ''}</td>
                      <td className="p-3 text-xs text-gray-500">{restricted ? (dist.gst_number ? maskGST(dist.gst_number) : '-') : (dist.gst_number || '-')}</td>
                      <td className="p-3 text-center"><Badge className="bg-orange-100 text-orange-700">{dist.discount_percentage ?? 0}%</Badge></td>
                      <td className="p-3 text-xs text-gray-400">{fmtDate(dist.created_at)}</td>
                      <td className="p-3 text-center">
                        {canManage && (
                          <div className="flex justify-center gap-1">
                            <Button size="sm" variant="ghost" className="text-blue-500 hover:text-blue-700 h-7 px-2" onClick={() => startEdit(dist)} data-testid={`edit-dist-${dist.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </Button>
                            <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" onClick={() => navigate(`/ledger-report?type=distributor&id=${dist.id}`)} data-testid={`ledger-dist-${dist.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 px-2" onClick={() => deleteDist(dist.id)} data-testid={`delete-dist-${dist.id}`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DistributorManagement;
